import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { WebUser, WebUserService } from '@summit/web-user-info';
import { AccessLevelEnum, ProfileTypeEnum } from '@summit/utils';
import { AppUrl, WebApplicationsService } from '@summit/application-list';

import { AuthService, UserExistsStatus } from 'src/app/auth.service';

import { environment } from 'src/environments/environment';
import { SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { UserExistsComponent } from './user-exists-dialog/user-exists.component';
import { NewUserCreationResponse } from '@summit/web-user-info/models/new-user-creation-response.model';

@Component({
  selector: 'profile-new-user',
  templateUrl: './new-user.component.html',
  styleUrls: ['./new-user.component.scss']
})
export class NewUserComponent implements OnInit {
  public webUser: WebUser = new WebUser('', null);

  public readonly accessLevelEnum = AccessLevelEnum;
  public readonly environment = environment;
  public profileType: string;
  public isUserValid: boolean;
  public isAdding: boolean;
  public isLoading: boolean;
  public canSave = true;
  public saveDisabledMessage: string;
  public errorMessage: string;
  public saveDisabledText: string;
  public hasProducerAccount: boolean;
  public hasAttemptedSubmit = 0;

  constructor(
    private webUserService: WebUserService,
    private authService: AuthService,
    private webAppsService: WebApplicationsService,
    private location: Location,
    private router: Router,
    private dialog: MatDialog
  ) { }

  public ngOnInit(): void {
    this.isLoading = false;
  }

  public save(): void {
    this.hasAttemptedSubmit++;
    if (this.isUserValid && !this.isAdding && this.webUser.pendingAccounts.length > 0) {
      this.isLoading = true;

      this.authService.checkIfUserExistsAndUpdateAccordingly(this.webUser)
        .subscribe((updateStatus: UserExistsStatus) => {
          let messageToDisplay: SafeHtml;
          if (updateStatus === UserExistsStatus.EXISTS) {
            // display a message indicating profile already exists with account(s) requested, and
            // show a dialog with the user's email address and link to User Management
            this.webAppsService.getAppUrl('USER_MANAGEMENT', [], environment.restfulService, environment.isNotLocalhost)
              .subscribe((appUrl: AppUrl) => {
                messageToDisplay = `<p>A web user profile already exists in our system with this email address.</p>` +
                  `<p>You can update the profile information from <a href="${appUrl.url}">User Management</a>.</p>`;
                this.displayExistingUserDialog(messageToDisplay);
              });
          } else if (updateStatus === UserExistsStatus.UPDATES_MADE) {
            // display a message indicating profile already exists, but updates were made to add the
            // requested account(s) that are not already associated with this profile, and
            // show a dialog with the user's email address and link to User Management
            this.webAppsService.getAppUrl('USER_MANAGEMENT', [], environment.restfulService, environment.isNotLocalhost)
              .subscribe((appUrl: AppUrl) => {
                messageToDisplay = `<p>A web user profile already exists in our system with this email address.</p>` +
                  `<p>We are merging your request with our existing record.</p>` +
                  `<p>Please go to <a href="${appUrl.url}">User Management</a> to review.</p>`;
                this.displayExistingUserDialog(messageToDisplay);
              });
          } else {
            this.webUserService.createUser(this.webUser, environment.restfulService, environment.isNotLocalhost)
              .subscribe((res: NewUserCreationResponse) => {
                if (res.creationSuccessful) {
                  if (this.webUser.profileType === ProfileTypeEnum.AGENT && !res.createdByAgencyAdmin) {
                      // We use a nested subscription here because we don't have to merge the observables together.
                      this.webUserService.sendAgencyAdminEmails(this.webUser, environment.restfulService, environment.isNotLocalhost)
                        .subscribe((emailSent: boolean) => {
                          if (!emailSent) {
                            console.log('Email to agency admin failed to send');
                          }
                          // Waits to navigates away until sendAgencyAdminEmails call completes
                          this.router.navigate(['/confirmation'], {queryParamsHandling: 'preserve'});
                      });
                  } else {
                    this.router.navigate(['/confirmation'], {queryParamsHandling: 'preserve'});
                  }
                } else {
                  // User was not created. Possibly due to duplicate email
                  this.isLoading = false;
                  this.canSave = false;
                  this.saveDisabledMessage = `Unable to create an account. If an account for this email address already exists, you'll receive an email to reset your password. Or you can try to <a href="/auth/forgotPassword">recover your password.</a> If you need additional assistance, call our help center at <a href="tel:+18002827648">1-800-282-7648</a>.`;
                }
              },
              err => {
                this.isLoading = false;
                this.errorMessage = err;
              }
            );
          }
        });

    } else {
      this.toggleSaveButton();
    }
  }

  public setIsAdding(isAdding: boolean): void {
    this.isAdding = isAdding;
    this.toggleSaveButton();
  }

  public setUserValid(isUserValid: boolean): void {
    this.isUserValid = isUserValid;
    this.toggleSaveButton();
  }

  public goBack(): void {
    this.location.back();
  }

  public updateProfileType(profileType: string): void {
    this.profileType = profileType;

    switch (profileType) {
      case ProfileTypeEnum.AGENT:
        this.saveDisabledText = 'an agency';
        break;
      case ProfileTypeEnum.INTERNAL:
        this.saveDisabledText = 'a department';
        break;
      case ProfileTypeEnum.PAYROLLCOMPANY:
        this.saveDisabledText = 'a company';
        break;
      case ProfileTypeEnum.INSURED:
        this.saveDisabledText = 'a policy';
        break;
      default: this.saveDisabledText = 'an account';
    }
  }

  private toggleSaveButton(): void {
    if (this.hasAttemptedSubmit > 0) {
      if (this.isUserValid && !this.isAdding) {
        this.canSave = true;
      } else {
        this.saveDisabledMessage = !this.isUserValid ?
                       'Please fill out all required fields before saving.' :
                       'You must choose ' + this.saveDisabledText + ' to associate with this user profile before submitting. '
                        + 'Be sure to click Select and then Add.';
        this.canSave = false;
      }
    }
  }

  private displayExistingUserDialog(messageToDisplay: SafeHtml): void {
    this.isLoading = false;
    const dialogRef = this.dialog.open(UserExistsComponent,
      {
        data: {
          messageToDisplay
        }
      }
    );

    dialogRef.afterClosed().subscribe(next => this.router.navigate(['/confirmation'], {queryParamsHandling: 'preserve'}));
  }
}
